import React, { Component, Suspense, lazy, Fragment } from 'react';
import { IAppProps } from './models/IAppProps';
import { IAppState } from './models/IAppState';
import { inject, observer } from 'mobx-react';
import Wrapper from './styledComponents/Wrapper';
import DebugInfoAction from '../shared/DebugInfoAction';
import NoDataPlaceholder from '@kurtosys/ksys-app-components/dist/components/base/NoDataPlaceholder';
import Disclaimer from '@kurtosys/ksys-app-components/dist/components/overview/Disclaimer';
import { InjectedStyledComponent } from '../shared/InjectedStyledComponent';
import Loading from '../shared/Loading';
import SkeletonLoader from '../shared/SkeletonLoader';
import Application from '@kurtosys/ksys-app-components/dist/components/base/Application';
import Card from '../Card/Card';
import AsOfDate from '../AsOfDate/AsOfDate';
import BarChart from '../BarChart/BarChart';
import Table from '../Table/Table';
import { IAppBreakpointProps } from './models/IAppConfiguration';
import Breakpoint from '@kurtosys/ksys-app-components/dist/components/base/Breakpoint';
import { IBreakpointProps } from '@kurtosys/ksys-app-components/dist/models/IBreakpointProps';

@inject('appStore', 'queryStore')
@observer
export class App extends Component<IAppProps, IAppState> {
	static configurationKey: 'app' = 'app';
	static styleKey: 'app' = 'app';
	constructor(props: IAppProps) {
		super(props);
	}

	getBreakpointComponentProps(breakpoint: IBreakpointProps<IAppBreakpointProps>, breakpointIndex: number): object {
		const { size, modifier } = breakpoint;
		const breakpointProps = {
			key: breakpointIndex,
			[size]: true,
			[modifier]: true,
		};
		return breakpointProps;
	}

	renderApp(props: IAppBreakpointProps) {
		const { show = {} } = props;
		const { chart: showChart = true, table: showTable = false } = show;
		return (
			<Fragment>
				<AsOfDate />
				{ showChart && <BarChart showTable={ showTable } /> }
				{ showTable && <Table /> }
			</Fragment>
		);
	}

	render() {
		const { className, appStore, selector, queryStore } = this.props;
		if (!appStore || !queryStore) {
			return null;
		}
		const { isBootstrapped, skeletonLoader, skeletonLoaders } = appStore;
		if (!isBootstrapped) {
			if (skeletonLoader) {
				return <SkeletonLoader config={ skeletonLoader } variables={ skeletonLoaders && skeletonLoaders.variables } />;
			}
			return <Loading />;
		}
		const { theme, show, showPlaceholder, noDataPlaceholder: noDataPlaceholderText, noDataPlaceholderDisclaimer, libraryComponentsConfiguration, appCardProps, getTranslateFunction, culture, assets, assetCacheOptions, assetRegisters, breakpoints = [], getAccessibilityTextFunction } = appStore;

		if (!show) {
			return null;
		}

		const noDataPlaceholder =
			(noDataPlaceholderDisclaimer && <Disclaimer { ...noDataPlaceholderDisclaimer } />) ||
			(noDataPlaceholderText && <NoDataPlaceholder>{ noDataPlaceholderText }</NoDataPlaceholder>);

		const hasBreakpoints = breakpoints.length > 0;

		const translate = getTranslateFunction();
		const accessibilityText = getAccessibilityTextFunction();

		return (
			<Application theme={ theme } configuration={ libraryComponentsConfiguration } translate={ translate } culture={ culture } assets={ assets } assetCacheOptions={ assetCacheOptions } assetRegisters={ assetRegisters } accessibilityText={ accessibilityText } analyticsHelper={ appStore.analyticsHelper }>
				<DebugInfoAction appStore={ appStore } />
				<Wrapper className={ className }>
					<Card { ...appCardProps }>
						{ showPlaceholder && noDataPlaceholder }
						{ !showPlaceholder && hasBreakpoints && (
							<Fragment>
								{breakpoints.map((breakpoint, breakpointIndex) => {
									const { props = {} } = breakpoint;
									const breakpointProps = this.getBreakpointComponentProps(breakpoint, breakpointIndex);
									return (
										<Breakpoint { ...breakpointProps }>
											<Wrapper className={ className }>
												{ this.renderApp(props) }
											</Wrapper>
										</Breakpoint>
									);
								}) }
							</Fragment>
						) }
						{
							!showPlaceholder && !hasBreakpoints && this.renderApp({})
						}
					</Card>
				</Wrapper>
			</Application>
		);
	}
}

export default InjectedStyledComponent(App, 'app');
